import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { rejectBanner } from "./userBannerApi";
import useUserAuthStore from "../../../zStore/userAuth";

const BannerRejectionModal = ({ isOpen, toggle, banner, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const { data: userData } = useUserAuthStore();

  // Get admin ID from user data
  const getAdminId = () => {
    if (!userData) return null;
    return userData.adminId || userData.admin_id || userData.userId || userData.user_id || null;
  };

  const validationSchema = Yup.object({
    reasons: Yup.array()
      .min(1, "Please select at least one reason")
      .required("Please select at least one reason"),
    comment: Yup.string()
      .required("Comment is required")
      .min(10, "Comment must be at least 10 characters"),
  });

  const formik = useFormik({
    initialValues: {
      reasons: [],
      comment: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const adminId = getAdminId();
      if (!adminId) {
        alert("Admin ID not found. Please log in again.");
        return;
      }

      setLoading(true);
      try {
        const rejection_reason = {
          by_admin: adminId,
          timestamp: new Date().toISOString(),
          reasons: values.reasons,
          comment: values.comment,
        };

        await rejectBanner({
          banner_id: banner?.id,
          rejection_reason,
        });

        if (onConfirm) {
          onConfirm();
        }
        toggle();
        formik.resetForm();
      } catch (error) {
        console.error("Error rejecting banner:", error);
        alert(error.response?.data?.message || error.message || "Failed to reject banner");
      } finally {
        setLoading(false);
      }
    },
  });

  const rejectionReasons = [
    "Inappropriate content",
    "Violence or harmful content",
    "Spam or misleading",
    "Copyright violation",
    "Low quality image",
    "Does not meet guidelines",
    "Other",
  ];

  const handleReasonChange = (reason) => {
    const currentReasons = formik.values.reasons;
    if (currentReasons.includes(reason)) {
      formik.setFieldValue(
        "reasons",
        currentReasons.filter((r) => r !== reason)
      );
    } else {
      formik.setFieldValue("reasons", [...currentReasons, reason]);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <form onSubmit={formik.handleSubmit}>
        <ModalHeader toggle={toggle} className="bg-danger text-white">
          <i className="mdi mdi-close-circle me-2"></i>Reject Banner Image
        </ModalHeader>
        <ModalBody>
          {banner && (
            <div className="mb-3">
              <p className="mb-1">Are you sure you want to reject this banner image?</p>
              <p className="mb-0">
                <strong>Banner ID:</strong> {banner.id}
              </p>
            </div>
          )}

          <FormGroup>
            <Label>
              Rejection Reasons <span className="text-danger">*</span>
            </Label>
            <div className="border rounded p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
              {rejectionReasons.map((reason) => (
                <div key={reason} className="form-check mb-2">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    id={`reason-${reason}`}
                    checked={formik.values.reasons.includes(reason)}
                    onChange={() => handleReasonChange(reason)}
                  />
                  <Label className="form-check-label" for={`reason-${reason}`}>
                    {reason}
                  </Label>
                </div>
              ))}
            </div>
            {formik.touched.reasons && formik.errors.reasons && (
              <div className="text-danger small mt-1">{formik.errors.reasons}</div>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              Comment <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows="4"
              name="comment"
              value={formik.values.comment}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter rejection comment..."
              invalid={formik.touched.comment && formik.errors.comment ? true : false}
            />
            {formik.touched.comment && formik.errors.comment && (
              <div className="text-danger small mt-1">{formik.errors.comment}</div>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="danger" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Rejecting...
              </>
            ) : (
              <>
                <i className="mdi mdi-close-circle me-2"></i>Reject
              </>
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default BannerRejectionModal;

